import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MenuComponent } from '../../shared/components/menu/menu.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { LoginValidationComponent } from '../../book-create/login-validation.component/login-validation.component';
import { AlertService, AlertType } from '../../shared/services/alert.service';
import { BookPublishEditService } from '../../shared/services/apis/platform/book-publish-edit.service';
import { BookPublishEditRequest } from '../../shared/services/requests/platform/book-publish-edit.request';
import { BookPublishEditResponse } from '../../shared/services/responses/platform/book-publish-edit.response';
import { KitStruct } from '../../shared/services/structs/book-publish-edit.struct';
import { BookCostCalculationResponse } from '../../shared/services/responses/platform/book-cost-calculation.response';
import { BookCostManagerService } from '../../shared/services/apis/platform/book-cost-manager.service';
import { firstValueFrom } from 'rxjs';
import { BookCostCalc, computeBreakdown, getMinPrice, toMoney } from './book-cost.calculator';
import { RedirectService } from '../../redirect-service';

type PendingUpload = {
  file: File;
  fileType: 'image' | 'video';
  chapterIndex: number;
  videoIndex?: number;
};

@Component({
  selector: 'app-book-publish-edit',
  standalone: true,
  templateUrl: './book-publish-edit.component.html',
  styleUrls: ['./book-publish-edit.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatSelectModule,
    MenuComponent,
    MatExpansionModule,
    FooterComponent,
    LoginValidationComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookPublishEditComponent implements OnInit {
  isLoading = false;
  idBookVersion!: number;

  @ViewChildren('imageInput') imageInputRefs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('videoInput') fileInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('thumbInput') thumbInputs!: QueryList<ElementRef<HTMLInputElement>>;

  request: BookPublishEditRequest = {
    idBookVersion: 0,
    listKitStruct: [],
    listVideoChapterStruct: [],
  };

  response!: BookPublishEditResponse;

  book: any;
  bookTitle = '';
  bookCoverUrl = '';
  bookVersion?: any;

  costCalc?: BookCostCalculationResponse;
  minBookPrice = 10;
  recommendedBookPrice = 0;

  private pendingUploads: PendingUpload[] = [];

  private readonly IMAGE_PLACEHOLDER = '';
  /** Limite de imagem (capa do capítulo e thumb do vídeo): 5 MB */
  private static readonly MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  /** Limite de imagem (capa do capítulo e thumb do vídeo): 200 MB */
  private static readonly MAX_VIDEO_BYTES = 200 * 1024 * 1024;

  constructor(
    private service: BookPublishEditService,
    private route: ActivatedRoute,
    private router: Router,
    private alert: AlertService,
    private bookCostService: BookCostManagerService,
    private cdr: ChangeDetectorRef,
    private redirectService: RedirectService
  ) {}

  ngOnInit(): void {
    this.idBookVersion = Number(this.route.snapshot.paramMap.get('idBookVersion'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  get hasAnyCourse(): boolean {
    const chapters = this.request?.listVideoChapterStruct ?? [];
    if (!chapters.length) return false;
    for (const ch of chapters) {
      const list = ch?.listVideoStruct ?? [];
      if (list.length) return true;
    }
    return false;
  }

  /** Regras:
   * - Se não houver capítulos/curso, desativar kits que selecionaram "video" e marcar aviso.
   * - Se passar a existir capítulos/curso, remove aviso (não reativa automaticamente — o usuário decide).
   */
  private enforceKitsAgainstCourseAvailability(): void {
    const hasCourse = this.hasAnyCourse;
    for (const kit of this.request?.listKitStruct ?? []) {
      const hasVideo = Array.isArray(kit?.selectedItems) && kit.selectedItems.includes('video');

      if (!hasCourse && hasVideo) {
        kit.isActive = false; // desativa
        (kit as any).__videoMissingWarn = true; // liga aviso
      } else {
        (kit as any).__videoMissingWarn = false; // limpa aviso quando voltar a ter curso
      }
    }
    this.cdr.markForCheck();
  }

  onLoginValidated(logged: boolean) {
    if (!logged) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.fetch();
  }

  private asBookCostCalc(): BookCostCalc {
    return {
      unitCost: this.costCalc?.unitCost ?? 0,
      platformProfitPercent: this.costCalc?.platformProfitPercent ?? 0,
      baseTaxPercent: this.costCalc?.baseTaxPercent ?? 0,
    };
  }

  /** Preview para imagem do capítulo (ordem de prioridade: blob -> url -> base64 -> placeholder) */
  getChapterImagePreview(chapter: any): string | null {
    if (!chapter) return null;

    if ((chapter as any).__imagePreviewUrl) return (chapter as any).__imagePreviewUrl;
    if (typeof chapter.imageUrlFile === 'string' && chapter.imageUrlFile) return chapter.imageUrlFile;

    if (typeof chapter.imageFileName === 'string' && chapter.imageFileName) {
      // Se vier como URL http(s) ou data:, usa direto.
      if (/^https?:\/\//i.test(chapter.imageFileName) || chapter.imageFileName.startsWith('data:')) {
        return chapter.imageFileName;
      }
    }
    return null;
  }

  /** Preview para thumbnail do vídeo (ordem: blob -> url -> base64 -> placeholder) */
  getVideoThumbPreview(video: any): string | null {
    if (!video) return null;
    if (video.__thumbPreviewUrl) return video.__thumbPreviewUrl; // blob local
    if (video.imageUrlFile) return video.imageUrlFile; // URL http/https
    if (video.imageFile) return `${video.imageFile}`;
    return null;
  }

  /** Revoga URLs blob antigas para não vazar memória */
  private revokeIfBlob(url?: string) {
    if (url && url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    }
  }

  // ===================================

  triggerFileInput(i: number, j: number) {
    const flatIndex = this.getFileInputIndex(i, j);
    const input = this.fileInputs.toArray()[flatIndex];
    input?.nativeElement?.click();
  }

  triggerThumbInput(i: number, j: number) {
    const flatIndex = this.getFileInputIndex(i, j);
    const input = this.thumbInputs.toArray()[flatIndex];
    input?.nativeElement?.click();
  }

  private getFileInputIndex(i: number, j: number): number {
    let index = 0;
    for (let ci = 0; ci < this.request.listVideoChapterStruct.length; ci++) {
      const chapter = this.request.listVideoChapterStruct[ci];
      if (ci < i) index += chapter.listVideoStruct.length;
      else if (ci === i) {
        index += j;
        break;
      }
    }
    return index;
  }

  onVideoImagePick(event: Event, i: number, j: number): void {
    const inputEl = event.target as HTMLInputElement;
    const file = inputEl.files?.[0];
    if (!file) return;

    // LIMITE DE 2 MB PARA THUMB
    if (file.size > BookPublishEditComponent.MAX_IMAGE_BYTES) {
      this.alert.show(
        'Imagem muito grande',
        'A miniatura do vídeo deve ter no máximo 5 MB.',
        AlertType.warning
      );
      inputEl.value = '';
      return;
    }

    this.pendingUploads.push({
      file,
      fileType: 'image',
      chapterIndex: i,
      videoIndex: j,
    });

    // Gera preview blob para o vídeo
    const video = this.request.listVideoChapterStruct[i].listVideoStruct[j] as any;
    this.revokeIfBlob(video.__thumbPreviewUrl);
    video.__thumbPreviewUrl = URL.createObjectURL(file);
    video.__thumbName = file.name;

    inputEl.value = '';
    this.cdr.markForCheck();
  }

  includesPhysicalBook(kit: any): boolean {
    return Array.isArray(kit?.selectedItems) && kit.selectedItems.includes('book');
  }

  triggerImageInput(index: number): void {
    const ref = this.imageInputRefs?.toArray?.()[index];
    ref?.nativeElement?.click();
  }

  // ======= CÁLCULOS (via book-cost.calculator.ts) =======

  getEstimatedValue(kit: any): number {
    const calc = this.asBookCostCalc();
    const price = toMoney(kit?.totalPrice);
    const hasPhysical = this.includesPhysicalBook(kit);
    return computeBreakdown(calc, price, hasPhysical).profit;
  }

  getEstimatedPercent(kit: any): number {
    const calc = this.asBookCostCalc();
    const price = toMoney(kit?.totalPrice);
    const hasPhysical = this.includesPhysicalBook(kit);
    return computeBreakdown(calc, price, hasPhysical).marginPctOnBox;
  }

  getEstimatedPercentLabel(kit: any): string {
    const pct = this.getEstimatedPercent(kit);
    return pct.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  }

  public getMinPriceForKit(kit: any): number {
    const calc = this.asBookCostCalc();
    const hasPhysical = this.includesPhysicalBook(kit);
    return getMinPrice(calc, hasPhysical, 10);
  }

  applyPriceGuards(kit: any): void {
    const min = this.getMinPriceForKit(kit);
    const val = toMoney(kit.totalPrice);
    if (val < min) kit.totalPrice = min;
    this.enforceKitsAgainstCourseAvailability();
  }

  private validateKitsPricing(): boolean {
    for (const kit of this.request?.listKitStruct || []) {
      if (!kit?.isActive) continue;
      this.applyPriceGuards(kit);
      const min = this.getMinPriceForKit(kit);
      if (toMoney(kit.totalPrice) < min) return false;
    }
    return true;
  }

  // ===================================

  fetch(): void {
    this.isLoading = true;

    this.service.getBookPublishEdit(this.idBookVersion).subscribe({
      next: (res: BookPublishEditResponse) => {
        if (res.isError) {
          this.alert.show('Erro', res.errorDescription ?? 'Erro ao carregar dados.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }

        if (!res.bookVersion) {
          this.alert.show('Erro', 'Versão do livro não encontrada.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }

        this.redirectService.redirectByStatus(
          res.bookVersion?.idStatusVersion,
          this.idBookVersion,
          res.bookVersion?.isCreditReviewing
        );

        this.book = res.bookVersion;
        this.request.idBookVersion = this.idBookVersion;
        this.request.listKitStruct =
          res.listKitStruct?.map(kit => ({
            ...kit,
            selectedItems: [
              ...(kit.includesBook ? ['book'] : []),
              ...(kit.includesEbook ? ['ebook'] : []),
              ...(kit.includesVideoAccess ? ['video'] : []),
            ],
            totalPrice: kit.totalPrice < 10 ? 10 : kit.totalPrice,
          })) ?? [];

        this.response = res;
        this.request.listVideoChapterStruct = res.listVideoChapterStruct ?? [];
        this.isLoading = false;
        this.bookVersion = res.bookVersion;

        if (!this.request.listKitStruct?.length) this.addEmptyKit();

        this.request.listVideoChapterStruct.forEach(chapter => {
          if (!chapter.listVideoStruct?.length) {
            chapter.listVideoStruct = [
              {
                title: '',
                description: '',
                videoFile: '',
                imageUrlFile: '',
                videoUrlFile: '',
                isSeen: false,
              },
            ];
          }
        });
        this.enforceKitsAgainstCourseAvailability();

        this.loadCostsForVersion(this.idBookVersion);
        this.cdr.markForCheck();
      },
      error: () => {
        this.alert.show('Erro', 'Erro inesperado ao carregar dados.', AlertType.error);
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadCostsForVersion(idBookVersion: number): void {
    this.bookCostService.calculate(idBookVersion).subscribe({
      next: res => {
        if (res.isError) return;
        this.costCalc = res;

        // Plataforma nunca é exibida isolada: entra junto com os impostos no piso do preço.
        const taxes = (res.platformValue ?? 0) + (res.taxValue ?? 0);
        const prod = (res.unitCost ?? 0) + res.marginValue;
        const minForPhysical = Math.max(10, taxes + prod);
        this.minBookPrice = toMoney(minForPhysical);

        this.recommendedBookPrice = toMoney(res.suggestedSalePrice ?? 0);
        this.cdr.markForCheck();
      },
    });
  }

  // ===== SUBMIT EM DUAS FASES =====
  async save(): Promise<void> {
    this.isLoading = true;
    this.enforceKitsAgainstCourseAvailability();

    // Valida se há kits ativos sem nenhum item selecionado
    for (const kit of this.request.listKitStruct) {
      if (!kit.isActive) continue;
      if (!kit.selectedItems?.length) {
        const kitLabel = kit.kitName?.trim() ? `"${kit.kitName.trim()}"` : 'sem nome';
        this.alert.show(
          'Item obrigatório',
          `O kit ${kitLabel} está ativo mas não tem nenhum item de venda selecionado. Selecione pelo menos um item (Livro físico, E-book ou Curso) ou desative o kit.`,
          AlertType.warning
        );
        this.isLoading = false;
        return;
      }
    }

    for (const kit of this.request.listKitStruct) {
      kit.includesBook = kit.selectedItems?.includes('book') || false;
      kit.includesEbook = kit.selectedItems?.includes('ebook') || false;
      kit.includesVideoAccess = kit.selectedItems?.includes('video') || false;
    }

    if (!this.validateKitsPricing()) {
      this.isLoading = false;
      return;
    }

    this.request.idBookVersion = this.idBookVersion;

    // 1) METADADOS
    try {
      const res = await firstValueFrom(this.service.postBookPublishEdit(this.request));
      if (res.isError) {
        this.alert.show('Erro', res.errorDescription ?? 'Erro ao salvar.', AlertType.error);
        this.isLoading = false;
        return;
      }
    } catch {
      this.alert.show('Erro', 'Erro inesperado ao salvar.', AlertType.error);
      this.isLoading = false;
      return;
    }

    // 2) REFRESH para obter IDs
    await new Promise<void>(resolve => {
      this.service.getBookPublishEdit(this.idBookVersion).subscribe({
        next: fresh => {
          if (!fresh.isError) {
            this.response = fresh;
            this.request.listVideoChapterStruct = fresh.listVideoChapterStruct ?? [];
            this.cdr.markForCheck();
          }
          resolve();
        },
        error: () => resolve(),
      });
    });

    // 3) UPLOADS PENDENTES
    try {
      for (const item of this.pendingUploads) {
        const chapter = this.request.listVideoChapterStruct[item.chapterIndex];
        if (!chapter || !chapter.idVideoChapter) continue;

        const form = new FormData();
        form.append('idBookVersion', String(this.idBookVersion));
        form.append('idVideoChapter', String(chapter.idVideoChapter));
        form.append('fileType', item.fileType);
        form.append('file', item.file, item.file.name);

        if (item.videoIndex != null) {
          const video = chapter.listVideoStruct[item.videoIndex] as any;
          const idVideo = video?.idVideo;
          if (idVideo) form.append('idVideo', String(idVideo));
        }

        const uploadRes = await firstValueFrom(this.service.uploadVideoFile(form));

        // Atualiza e também substitui previews blob por URLs do backend
        if (uploadRes && !uploadRes.isError) {
          if (item.videoIndex != null) {
            const v = chapter.listVideoStruct[item.videoIndex] as any;
            if (uploadRes.idVideo && !v.idVideo) v.idVideo = uploadRes.idVideo;

            if (uploadRes.imageUrlFile) {
              this.revokeIfBlob((chapter as any).__imagePreviewUrl);
              (chapter as any).__imagePreviewUrl = undefined;

              // garanta os dois campos
              (chapter as any).imageUrlFile = uploadRes.imageUrlFile;
              (chapter as any).imageFileName = uploadRes.imageUrlFile;
            }
            if (uploadRes.videoUrlFile) v.videoUrlFile = uploadRes.videoUrlFile;
          } else {
            if (uploadRes.imageUrlFile) {
              this.revokeIfBlob((chapter as any).__imagePreviewUrl);
              (chapter as any).__imagePreviewUrl = undefined;
              (chapter as any).imageUrl = uploadRes.imageUrlFile;
            }
          }
        }
      }

      this.alert.show('Sucesso', 'Alterações e arquivos salvos!', AlertType.success);
      this.pendingUploads = [];
      this.goBack();
    } catch {
      this.alert.show('Atenção', 'Metadados salvos. Alguns arquivos falharam no upload.', AlertType.warning);
    } finally {
      this.isLoading = false;
    }
  }

  // ===== Handlers que ENFILEIRAM os arquivos (+ preview capítulo) =====
  onImageUpload(event: Event, chapterIndex: number): void {
    const inputEl = event.target as HTMLInputElement;
    const file = inputEl.files?.[0];
    if (!file) return;

    // LIMITE DE 2 MB PARA CAPA DO CAPÍTULO
    if (file.size > BookPublishEditComponent.MAX_IMAGE_BYTES) {
      const maxMb = (BookPublishEditComponent.MAX_IMAGE_BYTES / (1024 * 1024)).toFixed(0);
      this.alert.show(
        'Tamanho da imagem',
        `O tamanho da imagem da capa deve ser de no máximo ${maxMb} MB.`,
        AlertType.warning
      );
      inputEl.value = '';
      return;
    }

    this.pendingUploads.push({
      file,
      fileType: 'image',
      chapterIndex,
    });

    const chapter = this.request.listVideoChapterStruct[chapterIndex] as any;
    // preview blob para capítulo
    this.revokeIfBlob(chapter.__imagePreviewUrl);
    chapter.__imagePreviewUrl = URL.createObjectURL(file);
    chapter.__imagePreviewName = file.name;

    inputEl.value = '';
    this.cdr.markForCheck();
  }

  onFileUpload(event: Event, i: number, j: number): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];

    // limite de tamanho já existente (usa MAX_VIDEO_BYTES)
    if (file.size > BookPublishEditComponent.MAX_VIDEO_BYTES) {
      const maxMb = (BookPublishEditComponent.MAX_VIDEO_BYTES / (1024 * 1024)).toFixed(0);
      this.alert.show(
        'Arquivo muito grande',
        `O vídeo/arquivo selecionado tem ${(file.size / (1024 * 1024)).toFixed(1)} MB. O limite é ${maxMb} MB.`,
        AlertType.warning
      );
      input.value = '';
      return;
    }

    this.pendingUploads.push({
      file,
      fileType: 'video',
      chapterIndex: i,
      videoIndex: j,
    });

    const video = this.request.listVideoChapterStruct[i].listVideoStruct[j] as any;

    // revoke do preview antigo (se houver) e cria um novo objectURL persistente
    if (
      video.__filePreviewUrl &&
      typeof video.__filePreviewUrl === 'string' &&
      video.__filePreviewUrl.startsWith('blob:')
    ) {
      try {
        URL.revokeObjectURL(video.__filePreviewUrl);
      } catch {}
    }
    video.__file = file; // <- mantém o File selecionado
    video.__fileName = file.name; // rótulo
    video.__filePreviewUrl = URL.createObjectURL(file); // <- usado no <video> e no abrir em nova aba

    input.value = '';
    this.cdr.markForCheck();
  }

  openVideoPreview(url: string | null | undefined, event: MouseEvent) {
    event.stopPropagation();
    if (this.isValidUrl(url)) window.open(String(url), '_blank');
  }

  isUrl(value: string | null | undefined): boolean {
    return typeof value === 'string' && /^https?:\/\//.test(value);
  }

  // --- ROTULAGEM: "arquivo carregado (.extensão)" ---
  /** Novo helper direto para o template */
  getLoadedFileLabel(video: any): string {
    const source: string =
      (video?.__fileName as string) || (video?.videoUrlFile as string) || (video?.videoFile as string) || '';

    if (!source) return '';

    const ext = this.extractExt(source);
    return ext ? `arquivo carregado (${ext})` : 'arquivo carregado';
  }

  /** Mantido por compatibilidade; agora redireciona para getLoadedFileLabel */
  getVideoInputLabel(_fileContent: string | undefined, video?: any): string {
    if (video) return this.getLoadedFileLabel(video);
    // fallback antigo (caso alguém ainda passe string)
    if (!_fileContent) return '';
    const ext = this.extractExt(_fileContent);
    return ext ? `arquivo carregado (${ext})` : 'arquivo carregado';
  }

  private extractExt(value: string): string {
    if (!value) return '';
    if (value.startsWith('data:')) {
      const m = value.match(/^data:([^;]+);/);
      if (m) {
        const mime = m[1].toLowerCase();
        const map: Record<string, string> = {
          'image/jpeg': '.jpg',
          'image/jpg': '.jpg',
          'image/png': '.png',
          'image/webp': '.webp',
          'image/gif': '.gif',
          'video/mp4': '.mp4',
          'video/webm': '.webm',
          'video/ogg': '.ogv',
          'video/x-msvideo': '.avi',
          'video/x-matroska': '.mkv',
          'application/pdf': '.pdf',
        };
        return map[mime] ?? '';
      }
    }
    if (this.isUrl(value)) {
      try {
        const u = new URL(value);
        const last = (u.pathname.split('/').pop() || '').split('?')[0].split('#')[0];
        const dot = last.lastIndexOf('.');
        return dot > -1 ? last.slice(dot).toLowerCase() : '';
      } catch {}
    }
    const name = value.split(/[\\/]/).pop() || value;
    const dot = name.lastIndexOf('.');
    return dot > -1 ? name.slice(dot).toLowerCase() : '';
  }

  openVideoInNewTab(url: string | null | undefined, event: MouseEvent): void {
    event.stopPropagation();
    if (this.isValidUrl(url)) window.open(String(url), '_blank');
  }

  isValidUrl(value: string | null | undefined): boolean {
    try {
      const url = new URL(value || '');
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }

  hasKitWithNoItems(): boolean {
    for (const kit of this.request?.listKitStruct || []) {
      if (!kit?.isActive) continue;
      if (!kit.selectedItems?.length) return true;
    }
    return false;
  }

  canSave(): boolean {
    //if (this.hasKitWithNoItems()) return false;
    for (const kit of this.request?.listKitStruct || []) {
      if (!kit?.isActive) continue;
      const min = this.getMinPriceForKit(kit);
      if (toMoney(kit.totalPrice) < min) return false;
    }
    return true;
  }

  addEmptyKit(): void {
    const newKit: KitStruct = {
      idKit: 0,
      kitName: '',
      kitDescription: '',
      includesEbook: false,
      includesVideoAccess: false,
      includesBook: false,
      bookPriceInKit: 0,
      ebookPriceInKit: 0,
      videoAccessPriceInKit: 0,
      totalPrice: 10,
      isActive: true,
    };
    this.request.listKitStruct.push(newKit);
  }

  goBack(): void {
    this.router.navigate(['/meus-livros']);
  }

  goToBook(): void {
    if (this.book?.bookUrl) this.router.navigateByUrl(this.book.bookUrl);
  }

  canActivateKit(kit: any): boolean {
    const wantsVideo = Array.isArray(kit?.selectedItems) && kit.selectedItems.includes('video');
    if (!wantsVideo) return true; // não depende de curso
    if ((kit as any).__videoMissingWarn) return false; // foi desativado por falta de curso
    if (!this.hasAnyCourse) return false; // ainda não há capítulos/vídeos
    return true;
  }

  toggleKitActive(index: number): void {
    const kit = this.request.listKitStruct[index];

    // se está inativo e o clique é para ativar, valide primeiro
    if (!kit.isActive) {
      if (!this.canActivateKit(kit)) {
        this.alert.show(
          'Atenção',
          'Não é possível reativar este kit: não há capítulos/vídeos cadastrados.',
          AlertType.warning
        );
        return;
      }
    }

    kit.isActive = !kit.isActive;
    this.cdr.markForCheck();
  }

  openPdf(): void {
    if (this.bookVersion && this.bookVersion.bookReviewedFileName) {
      window.open(this.bookVersion.bookReviewedFileName, '_blank');
    }
  }

  addKit(): void {
    this.request.listKitStruct.push({
      kitName: '',
      kitDescription: '',
      includesBook: false,
      includesEbook: false,
      includesVideoAccess: false,
      totalPrice: 10,
      isActive: true,
      selectedItems: [],
    });
    this.cdr.markForCheck();
  }

  removeKit(index: number): void {
    this.request.listKitStruct.splice(index, 1);
    this.cdr.markForCheck();
  }

  toggleKit(index: number): void {
    const kit = this.request.listKitStruct[index];
    kit.isActive = !kit.isActive;
    this.cdr.markForCheck();
  }

  addChapter(): void {
    this.request.listVideoChapterStruct.push({
      idVideoChapter: 0,
      name: '',
      imageFileName: '',
      description: '',
      listVideoStruct: [
        {
          title: '',
          description: '',
          imageFile: '',
          videoFile: '',
          imageUrlFile: '',
          videoUrlFile: '',
          isSeen: false,
        },
      ],
    });
    this.enforceKitsAgainstCourseAvailability();
    this.cdr.markForCheck();
  }

  removeChapter(index: number): void {
    this.request.listVideoChapterStruct.splice(index, 1);
    this.enforceKitsAgainstCourseAvailability();
    this.cdr.markForCheck();
  }

  addVideo(chapterIndex: number): void {
    this.request.listVideoChapterStruct[chapterIndex].listVideoStruct.push({
      title: '',
      description: '',
      imageFile: '',
      videoFile: '',
      imageUrlFile: '',
      videoUrlFile: '',
      isSeen: false,
    });
    this.enforceKitsAgainstCourseAvailability();
    this.cdr.markForCheck();
  }

  removeVideo(chapterIndex: number, videoIndex: number): void {
    this.request.listVideoChapterStruct[chapterIndex].listVideoStruct.splice(videoIndex, 1);
    this.enforceKitsAgainstCourseAvailability();
    this.cdr.markForCheck();
  }

  onCurrencyInput(event: Event, kit: any): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/\D/g, '');
    const numeric = parseFloat(raw) / 100;
    kit.totalPrice = isNaN(numeric) ? 0 : toMoney(numeric);
    input.value = this.formatCurrency(kit.totalPrice);
    this.cdr.markForCheck(); // <- importante com OnPush
  }

  onCurrencyBlur(kit: any): void {
    if (!kit.totalPrice) kit.totalPrice = 0;
    this.applyPriceGuards(kit);
    this.cdr.markForCheck(); // <- importante com OnPush
  }

  editVideo(chapterIndex: number, videoIndex: number): void {
    const video = this.request.listVideoChapterStruct[chapterIndex].listVideoStruct[videoIndex];
    this.alert.show('Edição de vídeo', `Você clicou em editar o vídeo: ${video.title}`, AlertType.warning);
  }

  formatCurrency(value: number | null): string {
    if (value == null) return '';
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    });
  }

  // retorna true se dá pra visualizar algo (url http(s), file ou base64)
  getVisualizable(video: any): boolean {
    return !!(video?.videoUrlFile || video?.__file || video?.videoFile);
  }

  // abre em nova aba; impede o clique de subir para o .upload-box
  openMediaInNewTab(video: any, ev?: MouseEvent) {
    ev?.preventDefault();
    ev?.stopPropagation();

    const { url, revoke } = this.pickMediaUrl(video);
    if (!url) return;

    const win = window.open(url, '_blank', 'noopener');
    if (!win || win.closed || typeof win.closed === 'undefined') {
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.click();
    }

    if (revoke) {
      setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      }, 10000);
    }
  }

  getPlayableUrl(video: any): string | null {
    const { url } = this.pickMediaUrl(video);
    return url && /^https?:\/\//i.test(url) ? url : null; // só embeda http(s)
  }

  openUrlInNewTab(url?: string, ev?: MouseEvent) {
    ev?.preventDefault();
    ev?.stopPropagation();
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  }
  /**
   * Decide a melhor URL visualizável:
   * - http(s) direto (video.videoUrlFile)
   * - File do input (video.__file)
   * - base64 em video.videoFile (dataURL ou só o payload)
   */
  private pickMediaUrl(video: any): { url: string | null; revoke: boolean } {
    // 0) Se já criamos um preview persistente ao selecionar o arquivo, use ele
    if (typeof video?.__filePreviewUrl === 'string' && video.__filePreviewUrl.startsWith('blob:')) {
      return { url: video.__filePreviewUrl, revoke: false }; // já gerenciado no onFileUpload
    }

    // 1) Se tiver File mas sem preview persistente, cria um objectURL temporário
    if (video?.__file instanceof File) {
      const objectUrl = URL.createObjectURL(video.__file);
      return { url: objectUrl, revoke: true }; // será revogado após abrir
    }

    // 2) URL direta do backend preferencial
    if (typeof video?.videoUrlFile === 'string' && /^https?:\/\//i.test(video.videoUrlFile)) {
      return { url: video.videoUrlFile, revoke: false };
    }

    // 3) Campo videoFile pode ser http(s), data: ou base64 "cru"
    if (typeof video?.videoFile === 'string' && video.videoFile.length > 0) {
      if (/^https?:\/\//i.test(video.videoFile)) {
        return { url: video.videoFile, revoke: false };
      }
      if (video.videoFile.startsWith('data:')) {
        return { url: video.videoFile, revoke: false };
      }
      const mime = this.detectMimeFromContext(video);
      try {
        const blob = this.base64ToBlob(video.videoFile, mime);
        const objectUrl = URL.createObjectURL(blob);
        return { url: objectUrl, revoke: true };
      } catch {
        return { url: null, revoke: false };
      }
    }

    return { url: null, revoke: false };
  }

  // heurística simples: se tiver thumb/arquivo de vídeo, usa video/mp4; se vier de “pdfUrlFile”/extensão .pdf, usa application/pdf
  private detectMimeFromContext(video: any): string {
    const name: string = video?.__file?.name || video?.__fileName || '';
    if (/\.pdf$/i.test(name) || /pdf/i.test(video?.mime || '')) return 'application/pdf';
    // default para vídeo
    return 'video/mp4';
  }

  private base64ToBlob(b64: string, mime: string): Blob {
    const byteChars = atob(b64);
    const len = byteChars.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = byteChars.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }

  getEstimatedClass(kit: any): string {
    const v = this.getEstimatedValue(kit);
    if (v < 0) return 'profit-negative';
    if (v === 0) return 'profit-zero';
    return 'profit-positive';
  }
}
